import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useUpdatePatientEngagementMutation } from '../../api/patientsApi';
import { useListDoctorsQuery } from '../../api/usersApi';
import { doctorDisplayName } from '../appointments/doctorColors';

// Backend RenewalPitch enum names → labels (excludes NotYet, which is the "unset" state).
export const RENEWAL_PITCH_OPTIONS = [
  { value: 'Physiotherapy', label: 'Physiotherapy' },
  { value: 'TotalTransformation', label: 'Total Transformation' },
  { value: 'FunctionalTraining', label: 'Functional Training' },
  { value: 'NotPitched', label: 'Not Pitched' },
];

/**
 * Compulsory renewal-pitch capture. Shown when completing a patient's 2nd-to-last session
 * is blocked by the backend (`RenewalPitchRequired`). Saves RenewalPitched + RenewalPitchedBy,
 * then `onSaved()` lets the caller retry the original completion.
 */
export default function RenewalPitchModal({ patientId, patientName, onClose, onSaved }) {
  const { data: doctors = [] } = useListDoctorsQuery();
  const [pitched, setPitched] = useState('');
  const [pitchedById, setPitchedById] = useState('');
  const [save, { isLoading }] = useUpdatePatientEngagementMutation();

  const submit = async (e) => {
    e.preventDefault();
    if (!pitched) { toast.error('Select the renewal plan that was pitched.'); return; }
    if (!pitchedById) { toast.error('Select the doctor who pitched the renewal.'); return; }
    try {
      await save({ id: patientId, renewalPitched: pitched, renewalPitchedById: pitchedById }).unwrap();
      toast.success('Renewal pitch recorded');
      onSaved?.();
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not save the renewal pitch.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header"><h2>Renewal pitch required</h2><button type="button" className="modal-close" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
            {patientName ? <strong>{patientName}</strong> : 'This patient'} is on their final sessions.
            Record the renewal pitch before completing this session.
          </p>
          <div className="form-group">
            <label className="form-label">Renewal pitched *</label>
            <select className="form-control" value={pitched} onChange={(e) => setPitched(e.target.value)}>
              <option value="">— Select plan —</option>
              {RENEWAL_PITCH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Renewal pitched by *</label>
            <select className="form-control" value={pitchedById} onChange={(e) => setPitchedById(e.target.value)}>
              <option value="">— Select doctor —</option>
              {doctors.map((d) => <option key={d.id} value={d.id}>{doctorDisplayName(d.fullName)}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>{isLoading ? 'Saving…' : 'Save & continue'}</button>
        </div>
      </form>
    </div>
  );
}

/** True when an API error is the renewal-pitch guard. */
export function isRenewalPitchRequired(err) {
  return err?.data?.error?.code === 'RenewalPitchRequired';
}
